import { sha256 } from "./canonical.js";
import { PROTOCOL_VERSION, TRUST_EVENT_PROTOCOL } from "./types.js";
const ID = /^[a-zA-Z0-9][a-zA-Z0-9._:-]{2,127}$/;
const DNS_NAME = /^(?=.{1,253}$)(?:\*\.)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/;
const DNS_SUFFIX = /^(?=.{1,253}$)(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/;
const TRUST_EVENT_TOPICS = new Set([
    "capability.observed",
    "capability.expired",
    "dependency.observed",
    "service.claimed",
    "identity.rotated",
    "trust_bundle.updated",
    "policy.version.available",
    "certificate.staged",
    "certificate.promoted",
    "incident.issuance_paused",
]);
const guardrailKeys = new Set([
    "mode",
    "max_requests_per_requester_5m",
    "max_unique_sans_per_requester_24h",
    "block_algorithm_downgrade",
    "max_validity_increase_percent",
    "history_lookback_days",
]);
function assertIssuanceGuardrails(value, label, partial = false) {
    if (!value || typeof value !== "object" || Array.isArray(value))
        throw new TypeError(`${label} issuance guardrails are invalid`);
    const guardrails = value;
    if (Object.keys(guardrails).some((key) => !guardrailKeys.has(key)))
        throw new TypeError(`${label} issuance guardrails contain an unknown field`);
    if (!partial || guardrails.mode !== undefined) {
        if (!new Set(["monitor", "enforce"]).has(guardrails.mode ?? ""))
            throw new TypeError(`${label} issuance guardrail mode is invalid`);
    }
    const integer = (field, minimum, maximum) => {
        const candidate = guardrails[field];
        if (partial && candidate === undefined)
            return;
        if (!Number.isInteger(candidate) || Number(candidate) < minimum || Number(candidate) > maximum)
            throw new TypeError(`${label} ${field} is invalid`);
    };
    integer("max_requests_per_requester_5m", 1, 1_000);
    integer("max_unique_sans_per_requester_24h", 1, 10_000);
    integer("max_validity_increase_percent", 0, 1_000);
    integer("history_lookback_days", 1, 90);
    if (!partial || guardrails.block_algorithm_downgrade !== undefined) {
        if (typeof guardrails.block_algorithm_downgrade !== "boolean")
            throw new TypeError(`${label} algorithm-downgrade guardrail is invalid`);
    }
    if (partial && Object.keys(guardrails).length === 0)
        throw new TypeError(`${label} issuance guardrails cannot be empty`);
}
export function assertTrustEvent(value, expected, now = new Date()) {
    if (!value || typeof value !== "object")
        throw new TypeError("trust event must be an object");
    const event = value;
    if (event.protocol !== TRUST_EVENT_PROTOCOL)
        throw new TypeError("unsupported trust event protocol");
    if (typeof event.event_id !== "string" || !/^tevt_[A-Za-z0-9_-]{16,86}$/.test(event.event_id))
        throw new TypeError("invalid trust event identity");
    if (typeof event.tenant_id !== "string" || !ID.test(event.tenant_id))
        throw new TypeError("invalid trust event tenant");
    if (!new Set(["mesh_node", "sectigo_edge"]).has(event.source ?? ""))
        throw new TypeError("invalid trust event source");
    if (typeof event.source_id !== "string" || event.source_id.length < 3 || event.source_id.length > 256 || /[\u0000-\u001f\u007f\s]/.test(event.source_id))
        throw new TypeError("invalid trust event source identity");
    if (!Number.isSafeInteger(event.sequence) || (event.sequence ?? 0) < 1)
        throw new TypeError("invalid trust event sequence");
    if (!TRUST_EVENT_TOPICS.has(event.topic))
        throw new TypeError("invalid trust event topic");
    if (typeof event.type !== "string" || !/^[a-z][a-z0-9_.-]{2,127}$/.test(event.type))
        throw new TypeError("invalid trust event type");
    const occurred = Date.parse(event.occurred_at ?? "");
    if (!Number.isFinite(occurred) || occurred > now.getTime() + 5 * 60_000)
        throw new TypeError("invalid trust event time");
    if (event.subject_id !== undefined && (typeof event.subject_id !== "string" || event.subject_id.length < 1 || event.subject_id.length > 256 || /[\u0000-\u001f\u007f]/.test(event.subject_id)))
        throw new TypeError("invalid trust event subject");
    if (!event.data || typeof event.data !== "object" || Array.isArray(event.data))
        throw new TypeError("invalid trust event data");
    try {
        if (JSON.stringify(event.data).length > 64 * 1024)
            throw new TypeError("invalid trust event data");
    }
    catch {
        throw new TypeError("invalid trust event data");
    }
    if (typeof event.source_event_sha256 !== "string" || !/^[A-Za-z0-9_-]{43}$/.test(event.source_event_sha256) || typeof event.previous_source_sha256 !== "string" || !(event.previous_source_sha256 === "genesis" || /^[A-Za-z0-9_-]{43}$/.test(event.previous_source_sha256)))
        throw new TypeError("invalid trust event source chain");
    if (event.event_id !== `tevt_${event.source_event_sha256}`)
        throw new TypeError("trust event identity is not bound to its source event");
    if (expected?.tenantId && event.tenant_id !== expected.tenantId)
        throw new TypeError("trust event tenant mismatch");
    if (expected?.source && event.source !== expected.source)
        throw new TypeError("trust event source mismatch");
    if (expected?.sourceId && event.source_id !== expected.sourceId)
        throw new TypeError("trust event source identity mismatch");
}
export function assertIssuanceTransaction(value, now = new Date()) {
    if (!value || typeof value !== "object")
        throw new TypeError("transaction must be an object");
    const tx = value;
    if (tx.protocol !== PROTOCOL_VERSION)
        throw new TypeError("unsupported protocol version");
    for (const [label, id] of [
        ["transaction_id", tx.transaction_id],
        ["tenant_id", tx.tenant_id],
        ["node_id", tx.node_id],
    ]) {
        if (typeof id !== "string" || !ID.test(id))
            throw new TypeError(`invalid ${label}`);
    }
    if (typeof tx.requester !== "string" || !tx.requester.startsWith("spiffe://")) {
        throw new TypeError("requester must be a SPIFFE ID");
    }
    if (!tx.request || typeof tx.request !== "object")
        throw new TypeError("request is required");
    if (!Array.isArray(tx.request.sans) || tx.request.sans.length === 0 || tx.request.sans.length > 100) {
        throw new TypeError("between 1 and 100 SANs are required");
    }
    for (const san of tx.request.sans) {
        if (!DNS_NAME.test(san))
            throw new TypeError(`invalid DNS SAN: ${san}`);
    }
    if (tx.request.validity_seconds < 300 || tx.request.validity_seconds > 31_536_000) {
        throw new TypeError("validity must be between 5 minutes and 365 days");
    }
    if (tx.request.deployment_mode && !new Set(["single", "dual_slot", "microsoft_adcs", "nginx", "apache", "java_keystore", "hashicorp_vault"]).has(tx.request.deployment_mode)) {
        throw new TypeError("unsupported deployment mode");
    }
    if (["nginx", "apache", "java_keystore", "hashicorp_vault"].includes(tx.request.deployment_mode ?? "")) {
        if (typeof tx.request.deployment_adapter_reference !== "string" ||
            !/^local\/[a-z0-9][a-z0-9._-]{2,121}$/.test(tx.request.deployment_adapter_reference) ||
            !Number.isSafeInteger(tx.request.deployment_integration_revision) ||
            (tx.request.deployment_integration_revision ?? 0) < 1) {
            throw new TypeError(`${tx.request.deployment_mode} deployment requires an exact local adapter and positive integration revision`);
        }
    }
    else if (tx.request.deployment_adapter_reference !== undefined || tx.request.deployment_integration_revision !== undefined) {
        throw new TypeError("deployment adapter binding is only valid for an activating adapter");
    }
    if (typeof tx.nonce !== "string" || tx.nonce.length < 24 || tx.nonce.length > 256) {
        throw new TypeError("invalid nonce");
    }
    if (typeof tx.customer_decision_sha256 !== "string" || !/^[A-Za-z0-9_-]{43}$/.test(tx.customer_decision_sha256)) {
        throw new TypeError("invalid customer decision digest");
    }
    if ((tx.capability_record_id === undefined) !== (tx.capability_record_sha256 === undefined) ||
        (tx.capability_record_id !== undefined && (typeof tx.capability_record_id !== "string" || !ID.test(tx.capability_record_id))) ||
        (tx.capability_record_sha256 !== undefined && !/^[A-Za-z0-9_-]{43}$/.test(tx.capability_record_sha256))) {
        throw new TypeError("capability evidence identity and digest must be bound together");
    }
    const issued = Date.parse(tx.issued_at ?? "");
    const expires = Date.parse(tx.expires_at ?? "");
    if (!Number.isFinite(issued) || !Number.isFinite(expires) || expires <= issued) {
        throw new TypeError("invalid transaction time window");
    }
    if (Math.abs(now.getTime() - issued) > 5 * 60_000)
        throw new TypeError("issued_at outside clock-skew window");
    if (now.getTime() >= expires)
        throw new TypeError("transaction expired");
}
export async function assertCustomerDecisionBinding(transaction, decision) {
    if (!/^[A-Za-z0-9_-]{43}$/.test(decision.policy_sha256)) {
        throw new TypeError("customer policy decision lacks a canonical policy content digest");
    }
    if (await sha256(decision) !== transaction.customer_decision_sha256) {
        throw new TypeError("customer policy decision is not bound to the signed transaction");
    }
}
export async function capabilityEvidenceDigest(record) {
    return sha256(record);
}
export async function assertCapabilityRecord(value, now = new Date()) {
    if (!value || typeof value !== "object")
        throw new TypeError("capability record must be an object");
    const record = value;
    for (const field of [record.id, record.tenant_id, record.endpoint_id]) {
        if (typeof field !== "string" || field.length < 3 || field.length > 256)
            throw new TypeError("invalid capability identity");
    }
    if (!new Set(["mesh_node_active", "mesh_node_passive", "sdk_self_report", "import"]).has(record.source)) {
        throw new TypeError("invalid capability source");
    }
    const observed = Date.parse(record.observed_at);
    const expires = Date.parse(record.expires_at);
    if (!Number.isFinite(observed) || !Number.isFinite(expires) || observed > now.getTime() + 60_000 || expires <= now.getTime() || expires - observed > 7 * 24 * 3600_000) {
        throw new TypeError("invalid capability evidence window");
    }
    if (!Array.isArray(record.tls_versions) || !Array.isArray(record.signature_algorithms) || !Array.isArray(record.key_establishment)) {
        throw new TypeError("invalid capability algorithms");
    }
    if (record.tls_versions.length > 8 || new Set(record.tls_versions).size !== record.tls_versions.length ||
        record.signature_algorithms.length > 16 || new Set(record.signature_algorithms).size !== record.signature_algorithms.length ||
        record.key_establishment.length > 8 || new Set(record.key_establishment).size !== record.key_establishment.length ||
        record.tls_versions.some((item) => !new Set(["TLS1.2", "TLS1.3"]).has(item)) ||
        record.signature_algorithms.some((item) => !new Set(["ECDSA-P256-SHA256", "ECDSA-P384-SHA384", "RSA-PSS-3072-SHA384", "ML-DSA-65", "SLH-DSA-SHA2-128s", "HYBRID-ECDSA-P384-ML-DSA-65"]).has(item)) ||
        record.key_establishment.some((item) => !new Set(["X25519", "X25519MLKEM768"]).has(item))) {
        throw new TypeError("capability algorithm identifiers must be supported and unique");
    }
    if (!record.application || !record.application.name || !record.application.version || !record.application.os) {
        throw new TypeError("invalid capability application");
    }
    if (["mesh_node_active", "mesh_node_passive"].includes(record.source)) {
        const measurement = record.measurements;
        const runtime = measurement?.runtime;
        if (measurement?.protocol !== "sharppki.capability-measurement.v1" ||
            typeof measurement.collector_version !== "string" || !/^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/.test(measurement.collector_version) ||
            !runtime || !/^go\d+\.\d+(?:\.\d+)?$/.test(runtime.go_version) ||
            !/^[a-z0-9_-]{2,32}$/.test(runtime.os) || !/^[a-z0-9_-]{2,32}$/.test(runtime.architecture) ||
            !new Set(["passed", "failed"]).has(runtime.ecdsa_p256_self_test) ||
            !new Set(["passed", "failed"]).has(runtime.mlkem768_self_test) ||
            runtime.os !== record.application.os) {
            throw new TypeError("Mesh Node capability evidence requires a valid measured runtime");
        }
        if (record.signature_algorithms.some((item) => item !== "ECDSA-P256-SHA256") ||
            (record.signature_algorithms.includes("ECDSA-P256-SHA256") && runtime.ecdsa_p256_self_test !== "passed")) {
            throw new TypeError("Mesh Node signature capability is not backed by its runtime self-test");
        }
        const transport = measurement.edge_transport;
        if (!transport) {
            if (record.tls_versions.length || record.key_establishment.length) {
                throw new TypeError("Mesh Node transport capabilities require negotiated transport evidence");
            }
        }
        else {
            const transportObserved = Date.parse(transport.observed_at);
            if (!Number.isFinite(transportObserved) || Math.abs(transportObserved - observed) > 5 * 60_000 ||
                transportObserved > now.getTime() + 60_000 || transport.tls_version !== "TLS1.3" ||
                !new Set(["X25519", "X25519MLKEM768"]).has(transport.key_exchange) ||
                typeof transport.cipher_suite !== "string" || !/^TLS_[A-Z0-9_]{8,96}$/.test(transport.cipher_suite) ||
                !new Set(["h2", "http/1.1"]).has(transport.alpn) ||
                !/^[A-Za-z0-9_-]{43}$/.test(transport.peer_spki_sha256) || transport.verified_peer !== true) {
                throw new TypeError("Mesh Node transport measurement is invalid or stale");
            }
            const expectedKeyEstablishment = transport.key_exchange === "X25519MLKEM768"
                ? ["X25519", "X25519MLKEM768"]
                : ["X25519"];
            if (JSON.stringify(record.tls_versions) !== JSON.stringify(["TLS1.3"]) ||
                JSON.stringify(record.key_establishment) !== JSON.stringify(expectedKeyEstablishment) ||
                (transport.key_exchange === "X25519MLKEM768" && runtime.mlkem768_self_test !== "passed")) {
                throw new TypeError("Mesh Node transport claims do not match the negotiated measurement");
            }
        }
    }
    const { evidence_sha256: _digest, ...evidence } = record;
    if (await capabilityEvidenceDigest(evidence) !== record.evidence_sha256)
        throw new TypeError("capability evidence digest mismatch");
    return record;
}
export function distinctApproverNodeIds(approvals) {
    return [...new Set(approvals.map((approval) => approval.key_id))].sort();
}
export function approvalQuorumSatisfied(approvals, required) {
    if (!Number.isInteger(required) || required < 1 || required > 5)
        throw new TypeError("approval quorum must be between 1 and 5");
    return distinctApproverNodeIds(approvals).length >= required;
}
function assertAlgorithmList(value, label, allowEmpty = false) {
    if (!Array.isArray(value) || (!allowEmpty && value.length === 0) || value.length > 32)
        throw new TypeError(`invalid ${label}`);
    if (value.some((item) => typeof item !== "string" || item.length < 2 || item.length > 64) || new Set(value).size !== value.length) {
        throw new TypeError(`${label} must contain unique algorithm identifiers`);
    }
}
export function assertPlatformPolicy(value, now = new Date()) {
    if (!value || typeof value !== "object")
        throw new TypeError("platform policy must be an object");
    const policy = value;
    if (typeof policy.id !== "string" || !ID.test(policy.id) || !Number.isInteger(policy.version) || (policy.version ?? 0) < 1 || !new Set(["active", "retired"]).has(policy.status ?? "")) {
        throw new TypeError("invalid platform policy identity, version, or status");
    }
    assertAlgorithmList(policy.signatures?.allowed, "platform allowed signatures");
    assertAlgorithmList(policy.signatures?.forbidden, "platform forbidden signatures", true);
    if (policy.signatures.allowed.some((item) => policy.signatures.forbidden.includes(item))) {
        throw new TypeError("platform allowed and forbidden signatures must be disjoint");
    }
    assertAlgorithmList(policy.key_establishment?.allowed, "platform allowed key establishment");
    const validity = policy.validity;
    if (!validity || !Number.isInteger(validity.max_seconds) || validity.max_seconds < 300 || validity.max_seconds > 31_536_000 ||
        !Number.isInteger(validity.offline_renewal_max_seconds) || validity.offline_renewal_max_seconds < 0 || validity.offline_renewal_max_seconds > validity.max_seconds) {
        throw new TypeError("invalid platform validity bounds");
    }
    const supportedModes = new Set(["single", "dual_slot", "microsoft_adcs", "nginx", "apache", "java_keystore", "hashicorp_vault"]);
    if (!Array.isArray(policy.allowed_deployment_modes) || policy.allowed_deployment_modes.length < 1 || policy.allowed_deployment_modes.length > supportedModes.size ||
        new Set(policy.allowed_deployment_modes).size !== policy.allowed_deployment_modes.length || policy.allowed_deployment_modes.some((mode) => !supportedModes.has(mode))) {
        throw new TypeError("platform deployment modes are invalid");
    }
    if (!Number.isInteger(policy.minimum_customer_approvals) || (policy.minimum_customer_approvals ?? 0) < 1 || (policy.minimum_customer_approvals ?? 0) > 5) {
        throw new TypeError("platform minimum customer approvals must be between 1 and 5");
    }
    if (typeof policy.require_capability_evidence !== "boolean")
        throw new TypeError("platform capability requirement is invalid");
    assertIssuanceGuardrails(policy.issuance_guardrails, "platform");
    const notBefore = Date.parse(policy.not_before ?? "");
    const expires = Date.parse(policy.expires_at ?? "");
    if (!Number.isFinite(notBefore) || !Number.isFinite(expires) || expires <= notBefore || notBefore > now.getTime() + 5 * 60_000 || expires <= now.getTime()) {
        throw new TypeError("invalid, inactive, or expired platform policy window");
    }
}
export function assertCryptoPolicy(value, now = new Date()) {
    if (!value || typeof value !== "object")
        throw new TypeError("policy must be an object");
    const policy = value;
    if (typeof policy.id !== "string" || !ID.test(policy.id) || typeof policy.tenant_id !== "string" || !ID.test(policy.tenant_id)) {
        throw new TypeError("invalid policy identity");
    }
    if (!Number.isInteger(policy.version) || (policy.version ?? 0) < 1 || !new Set(["draft", "active", "retired"]).has(policy.status ?? "")) {
        throw new TypeError("invalid policy version or status");
    }
    assertAlgorithmList(policy.signatures?.preferred, "preferred signatures");
    assertAlgorithmList(policy.signatures?.allowed, "allowed signatures");
    assertAlgorithmList(policy.signatures?.forbidden, "forbidden signatures", true);
    if (policy.signatures.preferred.some((item) => !policy.signatures.allowed.includes(item)))
        throw new TypeError("preferred signatures must be allowed");
    if (policy.signatures.forbidden.some((item) => policy.signatures.allowed.includes(item)))
        throw new TypeError("allowed and forbidden signatures must be disjoint");
    assertAlgorithmList(policy.key_establishment?.preferred, "preferred key establishment");
    assertAlgorithmList(policy.key_establishment?.allowed, "allowed key establishment");
    if (policy.key_establishment.preferred.some((item) => !policy.key_establishment.allowed.includes(item)))
        throw new TypeError("preferred key establishment must be allowed");
    const validity = policy.validity;
    if (!validity || !Number.isInteger(validity.default_seconds) || !Number.isInteger(validity.max_seconds) || !Number.isInteger(validity.offline_renewal_max_seconds) ||
        validity.default_seconds < 300 || validity.max_seconds < validity.default_seconds || validity.max_seconds > 31_536_000 ||
        validity.offline_renewal_max_seconds < 0 || validity.offline_renewal_max_seconds > validity.max_seconds) {
        throw new TypeError("invalid policy validity bounds");
    }
    const profiles = policy.workload_profiles;
    if (!profiles || typeof profiles !== "object" || Array.isArray(profiles) || Object.keys(profiles).length < 1 || Object.keys(profiles).length > 128) {
        throw new TypeError("policy must define between 1 and 128 workload profiles");
    }
    for (const [profileID, profile] of Object.entries(profiles)) {
        if (!ID.test(profileID) || !profile || profile.id !== profileID)
            throw new TypeError("workload profile identity is invalid");
        assertAlgorithmList(profile.allowed_algorithms, `${profileID} allowed algorithms`);
        if (profile.allowed_algorithms.some((algorithm) => !policy.signatures.allowed.includes(algorithm))) {
            throw new TypeError(`${profileID} algorithms must be allowed by enterprise policy`);
        }
        if (!Number.isInteger(profile.max_validity_seconds) || profile.max_validity_seconds < 300 || profile.max_validity_seconds > validity.max_seconds) {
            throw new TypeError(`${profileID} validity must be within enterprise policy`);
        }
        if (!Array.isArray(profile.allowed_spiffe_prefixes) || profile.allowed_spiffe_prefixes.length < 1 || profile.allowed_spiffe_prefixes.length > 64 ||
            new Set(profile.allowed_spiffe_prefixes).size !== profile.allowed_spiffe_prefixes.length ||
            profile.allowed_spiffe_prefixes.some((prefix) => typeof prefix !== "string" || prefix.length > 2048 || !prefix.startsWith("spiffe://") || !prefix.endsWith("/") || prefix.includes("%") || /[?#]/.test(prefix))) {
            throw new TypeError(`${profileID} SPIFFE prefixes must be unique canonical path prefixes`);
        }
        for (const prefix of profile.allowed_spiffe_prefixes) {
            let parsed;
            try {
                parsed = new URL(prefix);
            }
            catch {
                throw new TypeError(`${profileID} contains an invalid SPIFFE prefix`);
            }
            if (parsed.protocol !== "spiffe:" || !parsed.hostname || parsed.hostname !== parsed.hostname.toLowerCase() || parsed.pathname === "/" || parsed.port || parsed.username || parsed.password) {
                throw new TypeError(`${profileID} contains an invalid SPIFFE prefix`);
            }
        }
        if (!Array.isArray(profile.allowed_dns_suffixes) || profile.allowed_dns_suffixes.length < 1 || profile.allowed_dns_suffixes.length > 64 ||
            new Set(profile.allowed_dns_suffixes.map((suffix) => suffix.toLowerCase())).size !== profile.allowed_dns_suffixes.length ||
            profile.allowed_dns_suffixes.some((suffix) => typeof suffix !== "string" || suffix !== suffix.toLowerCase() || !DNS_SUFFIX.test(suffix))) {
            throw new TypeError(`${profileID} DNS suffixes must be unique lowercase DNS names`);
        }
        const modes = profile.allowed_deployment_modes;
        const supportedModes = new Set(["single", "dual_slot", "microsoft_adcs", "nginx", "apache", "java_keystore", "hashicorp_vault"]);
        if (!Array.isArray(modes) || modes.length < 1 || modes.length > supportedModes.size || new Set(modes).size !== modes.length || modes.some((mode) => !supportedModes.has(mode))) {
            throw new TypeError(`${profileID} deployment modes are invalid`);
        }
        const rotation = profile.rotation;
        if (profile.issuance_guardrails)
            assertIssuanceGuardrails(profile.issuance_guardrails, profileID, true);
        if (rotation) {
            if (!new Set(["in_place", "dual_slot"]).has(rotation.mode) ||
                !Number.isInteger(rotation.renew_before_seconds) || rotation.renew_before_seconds < 300 || rotation.renew_before_seconds > profile.max_validity_seconds ||
                !Number.isInteger(rotation.overlap_seconds) || rotation.overlap_seconds < 0 || rotation.overlap_seconds > 604_800 ||
                !Number.isInteger(rotation.health_check_grace_seconds) || rotation.health_check_grace_seconds < 10 || rotation.health_check_grace_seconds > 3600 ||
                !new Set(["automatic_after_health", "manual"]).has(rotation.promotion) ||
                !Number.isInteger(rotation.rollback_window_seconds) || rotation.rollback_window_seconds < 60 || rotation.rollback_window_seconds > 86_400) {
                throw new TypeError(`${profileID} rotation policy is invalid`);
            }
            if (rotation.mode === "dual_slot" && !modes.some((mode) => mode === "dual_slot" || mode === "nginx" || mode === "apache" || mode === "java_keystore" || mode === "hashicorp_vault")) {
                throw new TypeError(`${profileID} dual-slot rotation requires a dual-slot or activating deployment path`);
            }
            if (rotation.mode !== "dual_slot" && modes.some((mode) => mode === "dual_slot" || mode === "nginx" || mode === "apache" || mode === "java_keystore" || mode === "hashicorp_vault")) {
                throw new TypeError(`${profileID} dual-slot deployment paths require dual-slot rotation`);
            }
            if (rotation.promotion === "automatic_after_health" && !modes.some((mode) => mode === "nginx" || mode === "apache" || mode === "java_keystore" || mode === "hashicorp_vault")) {
                throw new TypeError(`${profileID} automatic promotion requires an activating deployment adapter`);
            }
        }
        if (typeof profile.require_capability_evidence !== "boolean")
            throw new TypeError(`${profileID} capability requirement is invalid`);
    }
    const quorum = policy.customer_approval_quorum;
    if (quorum) {
        if (!Number.isInteger(quorum.required) || quorum.required < 1 || quorum.required > 5)
            throw new TypeError("approval quorum must be between 1 and 5");
        if (quorum.eligible_node_ids) {
            if (quorum.eligible_node_ids.length < quorum.required || new Set(quorum.eligible_node_ids).size !== quorum.eligible_node_ids.length || quorum.eligible_node_ids.some((id) => !ID.test(id))) {
                throw new TypeError("eligible quorum nodes must be unique valid identities and meet the threshold");
            }
        }
    }
    if (policy.issuance_guardrails)
        assertIssuanceGuardrails(policy.issuance_guardrails, "enterprise");
    if (policy.transition && !new Set(["classical", "hybrid", "pqc"]).has(policy.transition.mode))
        throw new TypeError("invalid crypto transition mode");
    if (policy.transition?.pqc_required_after && !Number.isFinite(Date.parse(policy.transition.pqc_required_after)))
        throw new TypeError("invalid PQC transition date");
    const notBefore = Date.parse(policy.not_before ?? "");
    const expires = Date.parse(policy.expires_at ?? "");
    if (!Number.isFinite(notBefore) || !Number.isFinite(expires) || expires <= notBefore || notBefore > now.getTime() + 5 * 60_000 || expires <= now.getTime())
        throw new TypeError("invalid, inactive, or expired policy window");
}
export function assertDistinctPolicyActors(proposedBy, approvedBy) {
    if (!proposedBy.startsWith("user:") || !approvedBy.startsWith("user:"))
        throw new TypeError("immutable user subjects are required");
    if (proposedBy === approvedBy)
        throw new TypeError("policy proposer cannot approve the same change");
}
//# sourceMappingURL=validation.js.map